'use client'
import { useCallback, useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getSanitizedRedirectUrls, LOGIN_STATUS_MESSAGE, SESSION_EXPIRED_KEY } from './utils'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import { captureException } from '../../components/Sentry/utils'
import useLoginUser from '../../hooks/useLoginUser'
import { LoginVariantA } from '../LogIn/Variants/ExperimentA'
import { useCheckout } from '@src/quince-site-common/src/services/checkoutService/hooks/useCheckout'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import ForgotPasswordModal from '../../components/ForgotPasswordModal'
import { useRedirectToCheckoutIfApplicable } from './hooks/use-login-redirect'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { useLoginSideEffectsAndRedirect } from './hooks/use-login-sideeffects'
import {
  resendSignUpCode,
  createAuthHubListener,
  handleOAuthCompletion,
  useAuth,
} from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'react-i18next'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { clearStaleLogoutBroadcastForSignIn } from '@src/utils/cross-domain-auth.helper'
const StyledLogin = styled.div``

const LogIn = ({ location, data }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { t: tAccountPage } = useTranslation('accountPage')
  const { checkoutInstance: checkoutService, initialized } = useCheckout()
  const redirectToCheckoutIfApplicable = useRedirectToCheckoutIfApplicable()
  const oauthCompletedRef = useRef(false)

  const page = data.authenticationPage
  const { loginUser, onGoogleFederatedLogIn } = useLoginUser()
  const [serverError, setServerError] = useState()

  const loginDownStreamSideEffectsWithRedirect = useLoginSideEffectsAndRedirect(setServerError)

  const [unverifiedAccount, setUnverifiedAccount] = useState(false)
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState()
  const [resetPasswordModalState, setResetPasswordModalState] = useState(false)
  const params = new URLSearchParams(location.search)
  const resetPwPopup = params.get('reset-pw-popup')
  const resetPassword = params.get('reset-password')
  const sessionExpired = params.get('status') === SESSION_EXPIRED_KEY
  const isInResetPasswordFlow = resetPassword === 'true'
  const oauthRefLoading = useRef(false)
  const { isLoggedIn: isLoggedInAsync, isInitializing } = useAuth()

  useEffect(() => {

    if (isInResetPasswordFlow || isInitializing) return
    const isLoggedInSync = AuthUtil.isLoggedIn()
    // After Google OAuth, AuthUtil.isLoggedIn() (sync localStorage check) may lag behind
    // isLoggedInAsync (Zustand, set by initializeAuth). Use either for the logged-in guard.
    const isLoggedIn = isLoggedInSync || isLoggedInAsync

    if (isLoggedIn && !oauthRefLoading.current) {
      if (isLoggedInAsync) {
        setLoading(true)
        // URL params may not have redirecturi after OAuth round-trip (?code=... replaces original params).
        // Fall back to sessionStorage where GoogleSignIn.tsx saved the B2B return URL before redirect.
        let redirectUri = getSanitizedRedirectUrls()
        if (!redirectUri) {
          const storedRedirectUri = sessionStorage.getItem('redirecturi')
          if (storedRedirectUri) {
            redirectUri = JSON.parse(storedRedirectUri)
            sessionStorage.removeItem('redirecturi')
          }
        }

        redirectToCheckoutIfApplicable(redirectUri)
        setLoading(false)
      } else {

        window.logoutUser?.(false, false, true)
      }
    }
  }, [isInResetPasswordFlow, isLoggedInAsync, isInitializing, redirectToCheckoutIfApplicable])

  useEffect(() => {
    if (sessionExpired) {
      setServerError({ message: LOGIN_STATUS_MESSAGE[SESSION_EXPIRED_KEY] })
    }
  }, [sessionExpired])

  useEffect(() => {
    if (resetPwPopup === 'true') {
      setResetPasswordModalState(true)
    }
  }, [resetPwPopup])

  const resendVerificationEmail = async email => {
    try {
      const shouldIncludeLocale =
        typeof window !== 'undefined' && window.__debug?.isI18nEnabled && getLocalisationDataFromStorage()?.locale
      await resendSignUpCode(email?.toLowerCase(), {
        ...(shouldIncludeLocale
          ? {
              locale: getLocalisationDataFromStorage()?.locale?.toString(),
            }
          : {}),
      })
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'resend_verification',
        context: {
          login_email: email,
          lb_user_id: getlbUserId(),
        },
      })
    } catch (err) {
      captureException(err, { email }, { type: 'API_FAILURE', API: 'RESEND_VERIFICATION_EMAIL' })
      trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
        apiName: 'resendVerificationEmail',
        status: `failure`,
        context: {
          email: email,
          error: err.message,
          errorType: err.name,
        },
      })
    }
    setUnverifiedAccount(false)
    setVerificationEmailSent(true)
    setTimeout(() => {
      setVerificationEmailSent(false)
    }, 10000)
  }

  const backToLogin = () => {
    setServerError('')
  }

  const onGoogleLogIn = useCallback(
    async (user, callbackFn) => {

      setServerError('')
      setLoading(true)
      try {
        // Get the correct email from user object
        const userEmail = user?.attributes?.email || user?.username || user?.email

        setEmail(userEmail) // Set email for the component state
        await onGoogleFederatedLogIn(user)
        trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
          trackInternal: true,
          // @i18n-ignore
          category: 'login',
          action: 'account_login',
          context: {
            login_email: userEmail,
            lb_user_id: getlbUserId(),
            // @i18n-ignore
            login_method: 'SSO',
          },
        })
        mixpanelCl.identify({ email: userEmail, isTentativeEmail: false })
        let redirectUri = getSanitizedRedirectUrls()
        const storedRedirectUri = sessionStorage.getItem('redirecturi')
        if (storedRedirectUri) {
          redirectUri = JSON.parse(storedRedirectUri)
          sessionStorage.removeItem('redirecturi')
        }

        await loginDownStreamSideEffectsWithRedirect(redirectUri)
        setLoading(false)
        callbackFn()
      } catch (err) {
        if (err && err.code == 'PASSWORD_RESET_NEEDED') {
          captureException(
            err,
            { data },
            {
              type: 'PASSWORD_RESET_NEEDED',
              // @i18n-ignore
              API: 'LOGIN',
            },
          )
          router.push('?reset-pw-popup=true')
        }
        setLoading(false)
        setServerError(err)
        callbackFn()
      }
    },
    [onGoogleFederatedLogIn, loginDownStreamSideEffectsWithRedirect, data, router],
  )

  // Handle OAuth completion events
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const unsubscribe = createAuthHubListener(async ({ event }) => {

      if (event === 'signInWithRedirect') {
        oauthRefLoading.current = true

        if (oauthCompletedRef.current || loading) {

          return
        }

        try {
          const user = await handleOAuthCompletion()

          if (user && user.attributes && user.attributes.email) {

            await onGoogleLogIn(user, () => {
              console.info(`Google OAuth login completed`)
            })
            oauthCompletedRef.current = true // Mark as completed
          }
        } catch (error) {
          console.error('Error handling OAuth completion:', error)
          setServerError(error)
          oauthCompletedRef.current = false
        }
        oauthRefLoading.current = false
      }
    })

    return () => {
      // Reset the OAuth completion flag when component unmounts
      oauthCompletedRef.current = false
      return unsubscribe()
    }
  }, [onGoogleLogIn, loading])

  const openResetPasswordModal = email => {
    router.replace('?reset-pw-popup=true')
    setEmail(email)
  }

  const login = async data => {
    let { username, password, hasPasswordResetted = false } = data
    setServerError('')
    setLoading(true)

    clearStaleLogoutBroadcastForSignIn()

    if (username) {
      username = username?.toLowerCase()
    }
    setEmail(username)

    try {
      // Get customer access token
      // Sign in

      await loginUser(username, password, hasPasswordResetted)
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'account_login',
        context: {
          login_email: username,
          lb_user_id: getlbUserId(),
          // @i18n-ignore
          login_method: 'general',
        },
      })
      mixpanelCl.identify({ email: username, isTentativeEmail: false })
      let redirectUri = getSanitizedRedirectUrls()
      const storedRedirectUri = sessionStorage.getItem('redirecturi')
      if (storedRedirectUri) {
        redirectUri = JSON.parse(storedRedirectUri)
        sessionStorage.removeItem('redirecturi')
      }
      await loginDownStreamSideEffectsWithRedirect(redirectUri)
    } catch (err) {
      setLoading(false)
      const errorCodesToIgnore = new Set(['UserNotFoundException', 'NotAuthorizedException'])

      /** No need of capturing above error events because they are not genuine issues */
      if (!errorCodesToIgnore.has(err.code)) {
        captureException(
          err,
          { data },
          {
            type: 'API_FAILURE',
            // @i18n-ignore
            API: 'LOGIN',
          },
        )

        trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
          apiName: `login`,
          status: `failure`,
          context: {
            email: email,
            error: err.message,
            errorType: err.code || err.name,
          },
        })
      }

      if (err && err.code == 'UserNotFoundException') {
        err.message =
          'Hmm. We don\'t have that email on record. <br/> Please verify your email or <a href="/sign-up" style="text-decoration:underline;font-weight: 500">sign up</a> if your account does not exist.'
      } else if (err && err.code == 'UserNotConfirmedException') {
        try {
          setUnverifiedAccount(true)
          err.message = t('account_verification_required')
        } catch (err) {
          err.message = t('email_verification_resend_error')
          captureException(`User email not confirmed. Could not resend verification email ${username}`)
        }
      } else if (err && err.code == 'PASSWORD_RESET_NEEDED') {
        captureException(err, { data }, { type: 'PASSWORD_RESET_NEEDED', API: 'LOGIN' })
        router.push('?reset-pw-popup=true')
      } else if (err && err.code == 'InternalErrorException') {
        err.message = t('login_error_try_later')
      } else if (err && err.code == 'PasswordResetRequiredException') {
        err.message = t('login_failed_reset_password')
      } else if (err && err.code == 'NotAuthorizedException') {
        if (err.message.includes('attempts exceeded')) {
          err.message = tAccountPage('password_attempts_exceeded')
        } else {
          err.message = tAccountPage('incorrectPwd')
        }
      }
      setServerError(err)
    }
  }

  const onCloseForgotPassword = () => {
    router.replace('?reset-pw-popup=false')
    setResetPasswordModalState(false)
  }

  const forgotPasswordSubmitCallback = async (username, password) => {
    setResetPasswordModalState(false)
    await login({ username, password, hasPasswordResetted: true })
  }

  return (
    <>
      <StyledLogin>
        <AuthWrapper image={page.image} classNames="auth_wrapper_login">
          <LoginVariantA
            serverError={serverError}
            unverifiedAccount={unverifiedAccount}
            resendVerificationEmail={resendVerificationEmail}
            verificationEmailSent={verificationEmailSent}
            email={email}
            backToLogin={backToLogin}
            page={page}
            login={login}
            loading={loading}
            openResetPasswordModal={openResetPasswordModal}
            isInResetPasswordFlow={isInResetPasswordFlow}
          />
          <ForgotPasswordModal
            open={resetPasswordModalState}
            username={email}
            onClose={onCloseForgotPassword}
            onSubmitCallback={forgotPasswordSubmitCallback}
          />
        </AuthWrapper>
      </StyledLogin>
    </>
  )
}

export default LogIn
